import React, { useCallback } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, number, objectOf, shape, string } from 'prop-types'

import usePageView from 'Pods/Shared/Hooks/usePageView'
import I18n from 'Pods/Shared/I18n'
import SolidgatePaymentSection from 'Pods/Shared/SolidgatePaymentSection/SolidgatePaymentSection'

import { NOOP } from 'Utils/stableDefaults'

import CompanyDetails from './Components/CompanyDetails/CompanyDetails'
import CreateAccountStep from './Components/CreateAccountStep/CreateAccountStep'
import EmailStep from './Components/EmailStep/EmailStep'
import GuestBanner from './Components/GuestBanner/GuestBanner'
import LoginStep from './Components/LoginStep/LoginStep'
import OrderSummary from './Components/OrderSummary/OrderSummary'
import SubscriptionTerms from './Components/SubscriptionTerms/SubscriptionTerms'
import useCheckoutTracking from './Hooks/useCheckoutTracking'
import useSubscriptionCheckout, {
  STEP_CREATE_ACCOUNT,
  STEP_EMAIL,
  STEP_LOGIN,
} from './Hooks/useSubscriptionCheckout'

function SubscriptionCheckout({
  merchant = '',
  signature = '',
  paymentIntent = '',
  orderSummary = null,
  plans = null,
  returnPath = '/profile/settings#tab-settings-subscription',
  guestCheckoutPath = '/guests/sessions',
  locale = 'en',
  companyPurchaseEnabled = false,
  companyFormData = null,
  savedCompanyData = null,
  loginRequired = false,
  isGuest = false,
  guestEmail: initialGuestEmail = null,
  checkoutPerModelCopyEnabled = null,
}) {
  const {
    step,
    guestEmail,
    isExistingUser,
    isGuestCheckout,
    isPaymentStep,
    isUpdateMode,
    guestPaymentData,
    isFormValid,
    isPaymentProcessing,
    isPayDisabled,
    plans: mergedPlans,
    selectedPlanKey,
    planSummary,
    handlePlanChange,
    stableSdkIntent,
    handleEmailSubmit,
    handleLoginSuccess,
    handleLoginClick,
    handleGuestReset,
    handleFormReady,
    handleFormValidityChange,
    submitPaymentForm,
    handlePaymentProcessing,
    handlePaymentSuccess,
    handleAccountCompleted,
    handleCompanyApplied,
    handleCompanyReverted,
    handleCompanyToggleChange,
    handleRetry,
  } = useSubscriptionCheckout({
    plans,
    orderSummary,
    paymentIntent,
    signature,
    savedCompanyData,
    loginRequired,
    locale,
    returnPath,
    guestCheckoutPath,
    isGuest,
    initialGuestEmail,
  })

  const tracking = useCheckoutTracking({ isUpdateMode })

  usePageView(tracking.trackView)

  const activeMerchant = guestPaymentData?.merchant ?? merchant
  const activeSignature = stableSdkIntent?.signature || guestPaymentData?.signature || signature
  const activePaymentIntent = stableSdkIntent?.paymentIntent || guestPaymentData?.paymentIntent || paymentIntent
  const activeCompanyPurchaseEnabled = guestPaymentData?.companyPurchaseEnabled ?? companyPurchaseEnabled
  const activeCompanyFormData = guestPaymentData?.companyFormData ?? companyFormData
  const activeSavedCompanyData = guestPaymentData?.savedCompanyData ?? savedCompanyData

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const rootClassName = isUpdateMode
    ? 'subscription-checkout subscription-checkout--update'
    : 'subscription-checkout'

  return (
    <div className={rootClassName}>
      <div className="subscription-checkout__main">
        <div className="subscription-checkout__content">
          {step === STEP_EMAIL && (
            <EmailStep
              locale={locale}
              guestCheckoutPath={guestCheckoutPath}
              onSubmit={handleEmailSubmit}
              onLoginClick={handleLoginClick}
            />
          )}

          {step === STEP_LOGIN && (
            <LoginStep
              locale={locale}
              email={guestEmail || ''}
              showExistingAccountNotice={isExistingUser}
              onLoginSuccess={handleLoginSuccess}
            />
          )}

          {step === STEP_CREATE_ACCOUNT && (
            <CreateAccountStep
              email={guestEmail || ''}
              planName={orderSummary?.planName || ''}
              locale={locale}
              returnPath={returnPath}
              onCompleted={handleAccountCompleted}
            />
          )}

          {isPaymentStep && (
            <>
              {!isPaymentProcessing && (
                <>
                  {guestEmail && (
                    <GuestBanner
                      email={guestEmail}
                      locale={locale}
                      onReset={handleGuestReset}
                    />
                  )}

                  <h1 className="subscription-checkout__title">
                    {translate(isUpdateMode
                      ? 'subscription_checkout.update_title'
                      : 'subscription_checkout.title')}
                  </h1>
                  <p className="subscription-checkout__subtitle">
                    {translate(isUpdateMode
                      ? 'subscription_checkout.update_subtitle'
                      : 'subscription_checkout.subtitle')}
                  </p>
                </>
              )}

              <SolidgatePaymentSection
                merchant={activeMerchant}
                signature={activeSignature}
                paymentIntent={activePaymentIntent}
                returnPath={returnPath}
                locale={locale}
                onFormReady={handleFormReady}
                onFormValidityChange={handleFormValidityChange}
                onPaymentProcessing={handlePaymentProcessing}
                onPaymentSuccess={isGuestCheckout ? handlePaymentSuccess : NOOP}
                onRetry={handleRetry}
                isUpdateMode={isUpdateMode}
                translationKeyPrefix="subscription_checkout"
                tracking={tracking}
              />

              {activeCompanyPurchaseEnabled && (
                <CompanyDetails
                  locale={locale}
                  companyFormData={activeCompanyFormData}
                  onApplied={handleCompanyApplied}
                  onReverted={handleCompanyReverted}
                  onToggleChange={handleCompanyToggleChange}
                  savedCompanyData={activeSavedCompanyData}
                  isUpdateMode={isUpdateMode}
                />
              )}

              {isUpdateMode && (
                <div className="subscription-checkout__actions">
                  <Button type="secondary" href={returnPath}>
                    {translate('subscription_checkout.update_cancel_button')}
                  </Button>
                  <Button type="primary" onClick={submitPaymentForm} disabled={!isFormValid}>
                    {translate('subscription_checkout.update_save_button')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!isUpdateMode && (
        <div className="subscription-checkout__order-details">
          <OrderSummary
            locale={locale}
            onPay={submitPaymentForm}
            disabled={isPayDisabled}
            hidePay={!isPaymentStep}
            {...orderSummary}
            currentPrice={planSummary.currentPrice}
            vatAmount={planSummary.vatAmount}
            originalPrice={planSummary.originalPrice}
            selectedPlanKey={selectedPlanKey}
            renewalIntervalMonths={planSummary.renewalIntervalMonths}
            plans={mergedPlans}
            onPlanChange={handlePlanChange}
            perModelCopyEnabled={checkoutPerModelCopyEnabled}
          />
          <SubscriptionTerms
            monthlyPrice={planSummary.currentPrice}
            locale={locale}
            planKey={selectedPlanKey}
          />
        </div>
      )}
    </div>
  )
}

SubscriptionCheckout.propTypes = {
  merchant: string,
  signature: string,
  paymentIntent: string,
  orderSummary: shape({
    planName: string,
    features: arrayOf(string),
    originalPrice: number,
    currentPrice: number,
    vatAmount: number,
  }),
  plans: arrayOf(shape({
    planType: string.isRequired,
    isDefault: bool,
    orderSummary: shape({
      currentPrice: number,
      vatAmount: number,
    }),
    paymentIntent: string,
    signature: string,
  })),
  returnPath: string,
  guestCheckoutPath: string,
  locale: string,
  companyPurchaseEnabled: bool,
  companyFormData: shape({
    countries: arrayOf(arrayOf(string)),
    countriesWithStates: arrayOf(string),
    states: objectOf(arrayOf(arrayOf(string))),
  }),
  savedCompanyData: shape({
    companyName: string,
    companyCode: string,
    vatin: string,
    country: string,
    state: string,
    zipCode: string,
    city: string,
    streetAddress: string,
    isFromBusinessAccount: bool,
  }),
  loginRequired: bool,
  isGuest: bool,
  guestEmail: string,
  checkoutPerModelCopyEnabled: bool,
}

export default SubscriptionCheckout
